import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { calculateRuneCost, getSpellByType } from '../../constants/spells';
import { isProtected } from '../../utils/protection';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { StatusBanner } from '../shell/StatusBanner';
import { EffectRow } from '../shell/EffectRow';
import { isEffectBeneficialToOwner } from '../../utils/effectOwnerImpact';
import { formatEnumLabel } from '../../utils/format';
import type { MagicShellProps } from '../../pages/Magic';
import type { SpellType } from '../../types/magic';

const selectStyle = {
  minHeight: 44,
  backgroundColor: COLORS.bg,
  color: COLORS.text.primary,
  border: `1px solid ${COLORS.inputBorder}`,
} as const;

/**
 * SF-148: mobile card-based content for Magic. Purely presentational — success/failure
 * dispatch (toast vs. inline) lives in the page-level handlers (Magic.tsx), not here. No sticky
 * bottom bar: single scalar action (one spell, one target), same reasoning as Explore.
 */
export function MobileMagicShell({
  province,
  magicData,
  kingdomNumber,
  islandNumber,
  setKingdomNumber,
  setIslandNumber,
  handleChangeKingdom,
  isLoadingKingdom,
  kingdomMembers,
  selectedTarget,
  handleTargetChange,
  selectedTargetLabel,
  selectedTargetProtectionCountdown,
  spellGroups,
  selectedSpell,
  handleSpellChange,
  runeCost,
  isSubmitting,
  castBlockedReason,
  handleCastSpell,
}: MagicShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const insufficientRunes = runeCost > 0 && magicData.currentRunes < runeCost;

  return (
    <div className="space-y-3">
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold text-game-50 outline-none">
        Mystics
      </h1>

      <GlanceCard title="Wizardry">
        <MetricRow label="Wizards" value={formatNumber(magicData.wizards)} unit="" weight="primary" />
        <MetricRow label="WPA" value={magicData.wpa} unit="" weight="secondary" />
        <MetricRow label="Runes" value={formatNumber(magicData.currentRunes)} unit="runes" weight="secondary" />
        <MetricRow label="Mana" value={magicData.currentMana.toFixed(1)} unit="%" weight="secondary" />
      </GlanceCard>

      <GlanceCard title="Active Effects" icon="✨">
        {province.magic.activeEffects.length === 0 ? (
          <div className="text-sm" style={{ color: COLORS.text.tertiary }}>No active effects</div>
        ) : (
          province.magic.activeEffects.map((effect, idx) => (
            <EffectRow
              key={idx}
              name={formatEnumLabel(effect.effectType)}
              ticksRemaining={effect.ticksRemaining}
              indefinite={effect.indefinite}
              urgent={effect.ticksRemaining <= 1 && !effect.indefinite}
              beneficial={isEffectBeneficialToOwner(effect.effectType)}
            />
          ))
        )}
      </GlanceCard>

      <GlanceCard title="Target">
        <div className="flex items-center gap-2">
          <div className="flex flex-col gap-1 flex-1">
            <label htmlFor="mobile-magic-kingdom-input" className="text-xs" style={{ color: COLORS.text.tertiary }}>
              Kingdom
            </label>
            <input
              id="mobile-magic-kingdom-input"
              type="number"
              inputMode="numeric"
              placeholder="14"
              value={kingdomNumber}
              onChange={(e) => setKingdomNumber(e.target.value)}
              onFocus={(e) => e.target.select()}
              className="w-full rounded text-sm py-2 px-2"
              style={selectStyle}
            />
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <label htmlFor="mobile-magic-island-input" className="text-xs" style={{ color: COLORS.text.tertiary }}>
              Island
            </label>
            <input
              id="mobile-magic-island-input"
              type="number"
              inputMode="numeric"
              placeholder="40"
              value={islandNumber}
              onChange={(e) => setIslandNumber(e.target.value)}
              onFocus={(e) => e.target.select()}
              className="w-full rounded text-sm py-2 px-2"
              style={selectStyle}
            />
          </div>
        </div>
        <button
          onClick={handleChangeKingdom}
          disabled={isLoadingKingdom}
          className="w-full text-center py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
          style={{
            minHeight: 44,
            backgroundColor: 'transparent',
            border: `1px solid ${COLORS.link}`,
            color: COLORS.link,
          }}
        >
          {isLoadingKingdom ? 'Loading...' : 'Change Kingdom'}
        </button>

        <div className="flex flex-col gap-1">
          <label htmlFor="mobile-magic-target-select" className="text-xs" style={{ color: COLORS.text.tertiary }}>
            Target
          </label>
          <select
            id="mobile-magic-target-select"
            value={selectedTarget}
            onChange={(e) => handleTargetChange(e.target.value)}
            className="w-full rounded text-sm py-2 px-2"
            style={selectStyle}
          >
            <option value="">Nobody (Self-Cast)</option>
            {kingdomMembers.map((member) => {
              const memberProtected = isProtected(member.protectionExpiresAtTick, province.sync.currentTick);
              return (
                <option key={member.provinceId} value={member.provinceId}>
                  {member.provinceName} ({member.location}){memberProtected ? ' (Protected)' : ''}
                </option>
              );
            })}
          </select>
        </div>

        {selectedTargetProtectionCountdown && (
          <StatusBanner
            tone="protection"
            icon="🛡️"
            headline="Target is protected"
            detail={`${selectedTargetProtectionCountdown} — offensive spells cannot target this province yet. Intel spells (Crystal Ball, Crystal Eye) are exempt.`}
            live
          />
        )}
      </GlanceCard>

      <GlanceCard title="Cast Spell">
        <div className="flex flex-col gap-1">
          <label htmlFor="mobile-magic-spell-select" className="text-xs" style={{ color: COLORS.text.tertiary }}>
            Spell
          </label>
          <select
            id="mobile-magic-spell-select"
            value={selectedSpell}
            onChange={(e) => handleSpellChange(e.target.value as SpellType | '')}
            className="w-full rounded text-sm py-2 px-2"
            style={selectStyle}
          >
            <option value="">-- Select a Spell --</option>
            {spellGroups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.spells.map((spell) => {
                  const cost = calculateRuneCost(province.territory.totals.acres, spell);
                  return (
                    <option key={spell.type} value={spell.type}>
                      {spell.name} ({cost} Runes)
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </select>
        </div>

        {selectedSpell && (
          <p className="text-sm" style={{ color: COLORS.text.tertiary }}>
            {getSpellByType(selectedSpell)?.description}
          </p>
        )}

        <MetricRow
          label="Rune Cost"
          value={runeCost > 0 ? formatNumber(runeCost) : '—'}
          unit="runes"
          weight={runeCost > 0 ? 'primary' : 'tertiary'}
        />

        {insufficientRunes && (
          <div className="text-xs" style={{ color: COLORS.negative }}>
            Insufficient runes
          </div>
        )}

        <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
          Casting on: <span style={{ color: COLORS.text.primary }}>{selectedTargetLabel}</span>
        </div>

        {castBlockedReason && (
          <span id="mobile-cast-blocked-reason" className="text-xs" style={{ color: COLORS.text.tertiary }}>
            {castBlockedReason}
          </span>
        )}

        <button
          onClick={handleCastSpell}
          disabled={!!castBlockedReason || isSubmitting}
          aria-describedby={castBlockedReason ? 'mobile-cast-blocked-reason' : undefined}
          className="w-full text-center py-2.5 rounded-lg text-sm font-bold disabled:opacity-50"
          style={{
            minHeight: 44,
            backgroundColor: COLORS.btnPrimary,
            border: `1px solid ${COLORS.btnPrimaryHover}`,
            color: COLORS.text.primary,
          }}
        >
          {isSubmitting ? 'Casting…' : 'Cast Spell'}
        </button>
      </GlanceCard>
    </div>
  );
}
